import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Collapse,
  Chip,
  Stack,
  TextField,
  Button,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  AttachMoney as ClaimIcon,
  Info as InfoIcon,
  ThumbUpOffAlt as ThumbUpIcon,
  ThumbDownOffAlt as ThumbDownIcon,
  ThumbUp as ThumbUpFilled,
  ThumbDown as ThumbDownFilled,
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import CardContent from './base/CardContent';
import {
  BaseAction,
  CardContentProps,
  GenerateClaimPayload,
  CPTCode,
  ICD10Code,
  Modifier,
  ActionCommand,
  ActionCommandType,
} from '../../types';
import { RoundedButton } from '../../../../styles/CustomButtons';
import { useCardExpansion } from '../../store/hooks';
import { useTracking } from '../../../../context/TrackingContext';
import { EventType } from '../../../../types/events';

const CONTAINER_NAME = 'generate-claim-card';

interface GenerateClaimCardProps extends CardContentProps {
  action: BaseAction<'billing_codes'>;
}

const GenerateClaimCard: React.FC<GenerateClaimCardProps> = ({ action, onCommand }) => {
  const { isExpanded } = useCardExpansion();
  const expanded = isExpanded(action.action_id);
  const { trackEvent } = useTracking();

  const actionData = action.action_payload as GenerateClaimPayload;

  // State for editing and modal
  const [editedCPTCodes, setEditedCPTCodes] = useState<CPTCode[]>(actionData.cpt_codes);
  const [showJustificationModal, setShowJustificationModal] = useState(false);
  const [justificationFeedback, setJustificationFeedback] = useState<
    'helpful' | 'not_helpful' | null
  >(null);

  // Helper functions
  const getCPTCodes = () => actionData.cpt_codes;
  const getAllICD10Codes = () => {
    const allICD10s: ICD10Code[] = [];
    actionData.cpt_codes.forEach(cpt => {
      allICD10s.push(...cpt.icd10_codes);
    });
    // Remove duplicates based on code
    return allICD10s.filter(
      (icd, index, self) => index === self.findIndex(i => i.code === icd.code),
    );
  };

  const formatModifiers = (modifiers: Modifier[]) =>
    modifiers && modifiers.length > 0 ? ` | ${modifiers.map(m => m.code).join(', ')}` : '';

  const updateEditedCPTCode = (index: number, field: keyof CPTCode, value: any) => {
    const newCodes = editedCPTCodes.map((code, idx) => {
      if (idx === index) {
        return { ...code, [field]: value };
      }
      return code;
    });
    setEditedCPTCodes(newCodes);
  };

  const addNewCPTCode = () => {
    setEditedCPTCodes([
      ...editedCPTCodes,
      {
        code: '',
        name: '',
        icd10_codes: [],
        modifiers: [],
      },
    ]);
  };

  const removeCPTCode = (index: number) => {
    setEditedCPTCodes(editedCPTCodes.filter((_, i) => i !== index));
  };

  const addICD10ToCode = (cptIndex: number) => {
    const newCodes = editedCPTCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: [...code.icd10_codes, { code: '', name: '' }],
        };
      }
      return code;
    });
    setEditedCPTCodes(newCodes);
  };

  const removeICD10FromCode = (cptIndex: number, icd10Index: number) => {
    const newCodes = editedCPTCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: code.icd10_codes.filter((_, i) => i !== icd10Index),
        };
      }
      return code;
    });
    setEditedCPTCodes(newCodes);
  };

  const updateICD10 = (
    cptIndex: number,
    icd10Index: number,
    field: keyof ICD10Code,
    value: string,
  ) => {
    const newCodes = editedCPTCodes.map((code, idx) => {
      if (idx === cptIndex) {
        return {
          ...code,
          icd10_codes: code.icd10_codes.map((icd, icdIdx) => {
            if (icdIdx === icd10Index) {
              return { ...icd, [field]: value };
            }
            return icd;
          }),
        };
      }
      return code;
    });
    setEditedCPTCodes(newCodes);
  };

  const renderCPTChip = (code: CPTCode, index: number) => (
    <Tooltip key={`cpt-${index}`} title={code.name} arrow>
      <Chip
        label={`${code.code}${formatModifiers(code.modifiers)}`}
        variant="outlined"
        size="small"
        sx={{
          cursor: 'help',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Tooltip>
  );

  const renderICD10Chip = (code: ICD10Code, index: number) => (
    <Tooltip key={`icd10-${index}`} title={code.name} arrow>
      <Chip
        label={code.code}
        variant="outlined"
        size="small"
        sx={{
          cursor: 'help',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      />
    </Tooltip>
  );

  const expandableContent = (
    <Stack spacing={2}>
      {editedCPTCodes.map((cptCode, cptIndex) => (
        <Box
          key={cptIndex}
          sx={{
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, minWidth: 40 }}>
              CPT
            </Typography>

            <TextField
              label="Code"
              value={cptCode.code}
              onChange={e => updateEditedCPTCode(cptIndex, 'code', e.target.value)}
              size="small"
              fullWidth
            />

            <Tooltip title="Remove this CPT code" arrow>
              <IconButton
                onClick={() => removeCPTCode(cptIndex)}
                sx={{ ml: 1 }}
                aria-label="remove CPT code"
                color="primary"
              >
                <RemoveIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <TextField
            label="Modifiers (comma separated codes)"
            value={cptCode.modifiers.map(m => m.code).join(', ')}
            onChange={e => {
              const modifierCodes = e.target.value
                .split(',')
                .map(m => m.trim())
                .filter(m => m);
              const modifiers: Modifier[] = modifierCodes.map(code => ({
                code,
                name: '', // Name would need to be looked up or entered separately
              }));
              updateEditedCPTCode(cptIndex, 'modifiers', modifiers);
            }}
            size="small"
            fullWidth
            placeholder="e.g., 95, 25"
            sx={{ mb: 2 }}
          />

          {/* ICD-10 codes for this CPT */}
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            ICD-10 Codes:
          </Typography>
          {cptCode.icd10_codes.map((icd10, icd10Index) => (
            <Stack key={icd10Index} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <TextField
                label="Code"
                value={icd10.code}
                onChange={e => updateICD10(cptIndex, icd10Index, 'code', e.target.value)}
                size="small"
                fullWidth
              />
              <IconButton
                onClick={e => {
                  e.stopPropagation();
                  removeICD10FromCode(cptIndex, icd10Index);
                }}
                size="small"
                color="primary"
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <Button
            variant="text"
            onClick={e => {
              e.stopPropagation();
              addICD10ToCode(cptIndex);
            }}
            startIcon={<AddIcon />}
            size="small"
            sx={{ mt: 1 }}
          >
            Add ICD-10
          </Button>
        </Box>
      ))}

      <RoundedButton
        variant="outlined"
        onClick={addNewCPTCode}
        sx={{ alignSelf: 'flex-start', mt: 1 }}
        startIcon={<AddIcon />}
      >
        Add CPT Code
      </RoundedButton>
    </Stack>
  );

  const content = (
    <>
      <Typography variant="body2" color="text.secondary">
        {action.patient?.name}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          '> span': {
            whiteSpace: 'nowrap',
          },
          '> span:not(:last-child)': {
            '::after': {
              content: '"|"',
              display: 'inline-block',
              mx: 1,
            },
            [`@container ${CONTAINER_NAME} (max-width: 275px)`]: {
              display: 'block',
              '::after': {
                content: 'none',
              },
            },
          },
        }}
      >
        <span>{new Date(action.created_at).toLocaleDateString()}</span>
        <span>
          {new Date(action.created_at).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      </Typography>

      <Collapse in={!expanded} timeout="auto" unmountOnExit>
        {/* Suggested Codes */}
        <Box sx={{ mt: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Suggested Codes
          </Typography>

          {/* CPT Codes */}
          {getCPTCodes().length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}
              >
                CPT | Modifiers
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {getCPTCodes().map((code, index) => renderCPTChip(code, index))}
              </Stack>
            </Box>
          )}

          {/* ICD-10 Codes */}
          {getAllICD10Codes().length > 0 && (
            <Box>
              <Typography
                variant="body2"
                sx={{ fontSize: '0.8125rem', color: 'text.secondary', mb: 0.5, fontWeight: 600 }}
              >
                ICD-10
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {getAllICD10Codes().map((code, index) => renderICD10Chip(code, index))}
              </Stack>
            </Box>
          )}
        </Box>

        <Button
          variant="outlined"
          onClick={event => {
            event.stopPropagation();
            setShowJustificationModal(true);
          }}
          sx={{
            justifyContent: 'flex-start',
            textAlign: 'left',
            borderColor: 'info.main',
            color: 'white',
            backgroundColor: 'info.main',
            borderRadius: 2,
            px: 2,
            py: 1,
            mb: 2,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'info.dark',
              color: 'white',
              borderColor: 'info.dark',
            },
          }}
          fullWidth
        >
          <Typography
            variant="body2"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              textAlign: 'left',
              flexGrow: 1,
            }}
          >
            <strong>Why these codes?</strong> {actionData.justification}
          </Typography>
        </Button>
      </Collapse>
    </>
  );

  return (
    <>
      <CardContent
        containerName={CONTAINER_NAME}
        id={action.action_id}
        title="Generate Claim?"
        content={content}
        avatarIcon={<ClaimIcon />}
        onCommand={(command: ActionCommand) => {
          if (command.type === ActionCommandType.ACCEPT) {
            if (expanded) {
              // When expanded and accepting, send edited CPT codes
              onCommand({
                type: ActionCommandType.ACCEPT_WITH_EDITS,
                payload: { cpt_codes: editedCPTCodes },
              });
            } else {
              // Pass through normal accept when not expanded
              onCommand(command);
            }
          } else {
            // Pass through all other commands unchanged
            onCommand(command);
          }
        }}
        expandable
        expandableContent={expandableContent}
        isExpandableContentEditable={true}
      />

      {/* Justification Modal */}
      <Dialog
        open={showJustificationModal}
        onClose={() => setShowJustificationModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh',
          },
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Why these codes?
            </Typography>
            <IconButton
              onClick={() => setShowJustificationModal(false)}
              size="small"
              sx={{ color: 'text.secondary' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
            {actionData.justification}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              pl: 2,
              pr: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Was this justification helpful?
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => {
                  setJustificationFeedback('not_helpful');

                  // Track negative feedback
                  trackEvent(EventType.FEEDBACK_SUBMITTED, {
                    feedback_type: 'thumbs_down',
                    context: {
                      feature_name: 'billing_codes_justification',
                      resource_type: 'action',
                      resource_id: action.action_id,
                      action_type: action.action_type,
                      action_state: action.state,
                      page_location: 'justification_modal',
                      user_action: 'thumbs_down_click',
                      encounter_id: action.encounter_id || null,
                      patient_id: action.patient?.patient_id || null,
                    },
                    comment: null,
                    session_id: null,
                  });

                  setTimeout(() => setShowJustificationModal(false), 1000);
                }}
                sx={{
                  mx: 1,
                  '&:hover': { cursor: 'pointer' },
                  color: 'primary.light',
                }}
              >
                {justificationFeedback !== 'not_helpful' ? <ThumbDownIcon /> : <ThumbDownFilled />}
              </IconButton>
              <IconButton
                onClick={() => {
                  setJustificationFeedback('helpful');

                  // Track positive feedback
                  trackEvent(EventType.FEEDBACK_SUBMITTED, {
                    feedback_type: 'thumbs_up',
                    context: {
                      feature_name: 'billing_codes_justification',
                      resource_type: 'action',
                      resource_id: action.action_id,
                      action_type: action.action_type,
                      action_state: action.state,
                      page_location: 'justification_modal',
                      user_action: 'thumbs_up_click',
                      encounter_id: action.encounter_id || null,
                      patient_id: action.patient?.patient_id || null,
                    },
                    comment: null,
                    session_id: null,
                  });

                  setTimeout(() => setShowJustificationModal(false), 1000);
                }}
                sx={{
                  mx: 1,
                  '&:hover': { cursor: 'pointer' },
                  color: 'primary.light',
                }}
              >
                {justificationFeedback !== 'helpful' ? <ThumbUpIcon /> : <ThumbUpFilled />}
              </IconButton>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GenerateClaimCard;
